import { supabase } from '../config/supabase';
import { 
  UserProgress, 
  WorkoutSession, 
  ExerciseRecord, 
  WorkoutDay,
  CurrentWorkoutState 
} from '../types/workout';

export interface DatabaseUserProgress {
  id?: string;
  user_id: string;
  current_week: number;
  current_day: WorkoutDay;
  personal_records: Record<string, ExerciseRecord>;
  created_at?: string;
  updated_at?: string;
}

export interface DatabaseWorkoutSession {
  id?: string;
  user_id: string;
  session_id: string;
  date: string;
  week: number;
  day: WorkoutDay;
  exercises: ExerciseRecord[];
  duration?: number;
  notes?: string;
  completed: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DatabaseCurrentWorkout {
  id?: string;
  user_id: string;
  workout_state: CurrentWorkoutState;
  created_at?: string;
  updated_at?: string;
}

export class DatabaseService {
  private static readonly DEFAULT_USER_ID = 'default_user';

  /**
   * Get the current authenticated user ID
   */
  private static async getCurrentUserId(): Promise<string> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Authentication error:', error);
        throw new Error(`Authentication failed: ${error.message}`);
      }
      
      if (!user) {
        console.warn('No authenticated user found, using default user ID');
        return this.DEFAULT_USER_ID;
      }
      
      console.log('✅ Authenticated user ID:', user.id);
      return user.id;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  }

  /**
   * Get user progress from database
   */
  static async getUserProgress(userId?: string): Promise<UserProgress | null> {
    try {
      if (!userId) {
        userId = await this.getCurrentUserId();
      }
      
      console.log('🔍 Fetching user progress for user:', userId);
      
      // Don't filter by user_id - let RLS handle it
      const { data: progressData, error: progressError } = await supabase
        .from('user_progress')
        .select('*')
        .single();

      if (progressError) {
        console.log('🔍 Progress error details:', { code: progressError.code, message: progressError.message, details: progressError.details, hint: progressError.hint });
        
        if (progressError.code === 'PGRST116') {
          // No rows returned - user doesn't exist yet
          console.log('No user progress found for user:', userId);
          return null;
        } else if (progressError.message?.includes('Not Acceptable') || progressError.message?.includes('406') || progressError.code === '406') {
          // 406 Not Acceptable - usually means RLS is blocking access because no data exists
          console.log('No user progress found (406 - RLS blocking):', userId);
          return null;
        } else {
          console.error('Database error fetching user progress:', progressError);
          throw progressError;
        }
      }

      if (!progressData) {
        console.log('No progress data returned for user:', userId);
        return null;
      }

      console.log('✅ User progress found, fetching workout history...');

      // Get workout history - don't filter by user_id, let RLS handle it
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('workout_sessions')
        .select('*')
        .order('date', { ascending: false });

      if (sessionsError) {
        console.error('Error fetching workout sessions:', sessionsError);
        throw sessionsError;
      }

      // Convert database format to app format
      const workoutHistory: WorkoutSession[] = (sessionsData || []).map(session => ({
        id: session.session_id,
        date: session.date,
        week: session.week,
        day: session.day as WorkoutDay,
        exercises: session.exercises,
        duration: session.duration,
        notes: session.notes,
        completed: session.completed
      }));

      console.log('✅ Successfully loaded user progress with', workoutHistory.length, 'workout sessions');

      return {
        userId: progressData.user_id,
        currentWeek: progressData.current_week,
        currentDay: progressData.current_day as WorkoutDay,
        workoutHistory,
        personalRecords: progressData.personal_records || {}
      };
    } catch (error) {
      console.error('Error getting user progress:', error);
      throw error;
    }
  }

  /**
   * Save user progress to database
   */
  static async saveUserProgress(progress: UserProgress): Promise<void> {
    try {
      // Save user progress - RLS will automatically set user_id
      const { error: progressError } = await supabase
        .from('user_progress')
        .upsert({
          user_id: progress.userId,
          current_week: progress.currentWeek,
          current_day: progress.currentDay,
          personal_records: progress.personalRecords,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (progressError) {
        throw progressError;
      }

      // Save workout sessions (upsert each one)
      for (const session of progress.workoutHistory) {
        const { error: sessionError } = await supabase
          .from('workout_sessions')
          .upsert({
            user_id: progress.userId,
            session_id: session.id,
            date: session.date,
            week: session.week,
            day: session.day,
            exercises: session.exercises,
            duration: session.duration,
            notes: session.notes,
            completed: session.completed,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,session_id'
          });

        if (sessionError) {
          throw sessionError;
        }
      }
    } catch (error) {
      console.error('Error saving user progress:', error);
      throw error;
    }
  }

  /**
   * Save a single workout session
   */
  static async saveWorkoutSession(session: WorkoutSession, userId?: string): Promise<void> {
    if (!userId) {
      userId = await this.getCurrentUserId();
    }
    try {
      const { error } = await supabase
        .from('workout_sessions')
        .upsert({
          user_id: userId,
          session_id: session.id,
          date: session.date,
          week: session.week,
          day: session.day,
          exercises: session.exercises,
          duration: session.duration,
          notes: session.notes,
          completed: session.completed,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,session_id'
        });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error saving workout session:', error);
      throw error;
    }
  }

  /**
   * Get recent workout sessions
   */
  static async getRecentWorkouts(limit: number = 10, userId?: string): Promise<WorkoutSession[]> {
    if (!userId) {
      userId = await this.getCurrentUserId();
    }
    try {
      // Don't filter by user_id - let RLS handle it
      const { data, error } = await supabase
        .from('workout_sessions')
        .select('*')
        .order('date', { ascending: false })
        .limit(limit);

      if (error) {
        if (error.message?.includes('Not Acceptable') || error.message?.includes('406')) {
          // 406 Not Acceptable - usually means RLS is blocking access because no data exists
          console.log('No workout sessions found (406 - RLS blocking):', userId);
          return [];
        }
        throw error;
      }

      return (data || []).map(session => ({
        id: session.session_id,
        date: session.date,
        week: session.week,
        day: session.day as WorkoutDay,
        exercises: session.exercises,
        duration: session.duration,
        notes: session.notes,
        completed: session.completed
      }));
    } catch (error) {
      console.error('Error getting recent workouts:', error);
      throw error;
    }
  }

  /**
   * Delete a workout session
   */
  static async deleteWorkoutSession(sessionId: string, userId?: string): Promise<void> {
    if (!userId) {
      userId = await this.getCurrentUserId();
    }
    try {
      const { error } = await supabase
        .from('workout_sessions')
        .delete()
        .eq('session_id', sessionId);

      if (error) {
        throw error;
      }
      
      console.log('✅ Workout session deleted successfully:', sessionId);
    } catch (error) {
      console.error('Error deleting workout session:', error);
      throw error;
    }
  }

  /**
   * Save current workout state
   */
  static async saveCurrentWorkoutState(state: CurrentWorkoutState, userId?: string): Promise<void> {
    if (!userId) {
      userId = await this.getCurrentUserId();
    }
    
    try {
      const { error } = await supabase
        .from('current_workout_state')
        .upsert({
          user_id: userId,
          workout_state: state,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving current workout state:', error);
        throw error;
      }
      
      console.log('✅ Current workout state saved successfully');
    } catch (error) {
      console.error('Error saving current workout state:', error);
      throw error;
    }
  }

  /**
   * Get current workout state
   */
  static async getCurrentWorkoutState(userId?: string): Promise<CurrentWorkoutState | null> {
    try {
      if (!userId) {
        userId = await this.getCurrentUserId();
      }
      
      console.log('🔍 Fetching current workout state for user:', userId);
      
      // Don't filter by user_id - let RLS handle it
      const { data, error } = await supabase
        .from('current_workout_state')
        .select('*')
        .single();

      if (error) {
        console.log('🔍 Error details:', { code: error.code, message: error.message, details: error.details, hint: error.hint });
        
        if (error.code === 'PGRST116') {
          // No rows returned - no current workout state
          console.log('No current workout state found for user:', userId);
          return null;
        } else if (error.message?.includes('Not Acceptable') || error.message?.includes('406') || error.code === '406') {
          // 406 Not Acceptable - usually means RLS is blocking access because no data exists
          console.log('No current workout state found (406 - RLS blocking):', userId);
          return null;
        } else {
          console.error('Error fetching current workout state:', error);
          throw error;
        }
      }

      console.log('✅ Current workout state found for user:', userId);
      return data ? data.workout_state : null;
    } catch (error: any) {
      console.error('Error getting current workout state:', error);
      console.log('🔍 Catch block error details:', { code: error?.code, message: error?.message, status: error?.status });
      
      // Handle 406 errors gracefully - return null instead of throwing
      if (error?.message?.includes('Not Acceptable') || error?.message?.includes('406') || error?.status === 406) {
        console.log('⚠️ 406 error caught in catch block - returning null');
        return null;
      }
      
      // Return null instead of throwing to prevent app crashes for missing table
      console.log('⚠️ Falling back to null - table may not exist yet');
      return null;
    }
  }

  /**
   * Clear current workout state
   */
  static async clearCurrentWorkoutState(userId?: string): Promise<void> {
    if (!userId) {
      userId = await this.getCurrentUserId();
    }
    try {
      // RLS will automatically limit to user's data, so we can delete without filtering
      const { error } = await supabase
        .from('current_workout_state')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (RLS will limit to user's data)

      if (error) {
        console.error('Error clearing current workout state:', error);
        throw error;
      }
      
      console.log('✅ Current workout state cleared successfully');
    } catch (error) {
      console.error('Error clearing current workout state:', error);
      // Don't throw to prevent app crashes for missing table
      console.log('⚠️ Failed to clear - table may not exist yet');
    }
  }

  /**
   * Reset all user data
   */
  static async resetUserData(userId?: string): Promise<void> {
    if (!userId) {
      userId = await this.getCurrentUserId();
    }
    try {
      // Delete all workout sessions (RLS will limit to user's data)
      const { error: sessionsError } = await supabase
        .from('workout_sessions')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (sessionsError) {
        throw sessionsError;
      }

      // Delete current workout state (RLS will limit to user's data)
      const { error: stateError } = await supabase
        .from('current_workout_state')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (stateError) {
        throw stateError;
      }

      // Delete user progress (RLS will limit to user's data)
      const { error: progressError } = await supabase
        .from('user_progress')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (progressError) {
        throw progressError;
      }
    } catch (error) {
      console.error('Error resetting user data:', error);
      throw error;
    }
  }

  /**
   * Test database connection
   */
  static async testConnection(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('count')
        .limit(1);

      if (error) {
        console.error('Database connection test failed:', error);
        return false;
      }

      console.log('✅ Database connection successful');
      return true;
    } catch (error) {
      console.error('Database connection test failed:', error);
      return false;
    }
  }
} 